import type { LexData } from "./types";

export type TransparencyKind = "decision" | "mandate" | "outreach" | "cooperation" | "admission";

export interface TransparencyEntry {
  id: string;
  kind: TransparencyKind;
  title: string;
  detail: string;
  outcome: string;
  occurredAt: number;
  href?: string;
}

export type RegistryProjectStatus = "active" | "charter" | "declined";

export interface RegistryCommit {
  id: string;
  label: string;
  summary: string;
  status: "merged" | "declined" | "pending";
  authorFirm: string;
  occurredAt: number;
}

export interface RegistryProject {
  id: string;
  title: string;
  summary: string;
  status: RegistryProjectStatus;
  deadline: number;
  patronFirms: string[];
  commits: RegistryCommit[];
  href: string;
}
export interface TransparencySummary {
  decisionsAdopted: number;
  decisionsRejected: number;
  mandatesApproved: number;
  outreachLogged: number;
  cooperationsRecorded: number;
  admissions: number;
  activeProjectLimit: number;
  activeProjectCount: number;
  projects: RegistryProject[];
  entries: TransparencyEntry[];
}

/**
 * Member-facing transparency register: the institutional memory the brief asks
 * for. It records decisions, approved lobbying mandates, logged outreach,
 * cooperation volume and admissions — without client names, matter values or
 * fees. Read-only; it is the audit trail members can actually see.
 */
export function buildTransparencyRegister(data: LexData): TransparencySummary {
  const entries: TransparencyEntry[] = [];
  let decisionsAdopted = 0;
  let decisionsRejected = 0;

  for (const snapshot of data.voteResultSnapshots) {
    const proposal = data.proposals.find((item) => item.id === snapshot.proposalId);
    const discussion = proposal ? data.discussions.find((item) => item.id === proposal.discussionId) : undefined;
    if (snapshot.finalOutcome === "adopted") decisionsAdopted += 1;
    if (snapshot.finalOutcome === "rejected") decisionsRejected += 1;
    entries.push({
      id: `decision-${snapshot.id}`,
      kind: "decision",
      title: discussion?.title ?? "Governance decision",
      detail: `${proposal?.category ?? "ordinary"} · quorum ${snapshot.quorumMet ? "met" : "not met"} · support ${(snapshot.scoreBalance * 100).toFixed(0)}%`,
      outcome: snapshot.finalOutcome === "adopted" ? "Adopted" : snapshot.finalOutcome === "rejected" ? "Rejected" : "No quorum",
      occurredAt: snapshot.certifiedAt ?? snapshot.createdAt,
      href: proposal ? `/discussions/proposals/${proposal.id}` : undefined,
    });
  }

  const mandatesApproved = data.lobbyingProjects.filter((project) => project.mandateStatus === "approved" || project.mandateStatus === "emergency").length;
  for (const project of data.lobbyingProjects.filter((item) => item.mandateStatus === "approved" || item.mandateStatus === "emergency")) {
    entries.push({
      id: `mandate-${project.id}`,
      kind: "mandate",
      title: project.title,
      detail: `Targets: ${project.targetInstitutions.join(", ") || "—"}`,
      outcome: project.mandateStatus === "emergency" ? "Emergency mandate" : "Mandate approved",
      occurredAt: project.updatedAt,
      href: "/legislative-action",
    });
  }

  const outreachEvents = data.activityEvents.filter((event) => event.type === "outreach logged");
  for (const event of outreachEvents) {
    entries.push({ id: `outreach-${event.id}`, kind: "outreach", title: event.title, detail: event.summary, outcome: "Outreach logged", occurredAt: event.createdAt, href: "/legislative-action" });
  }

  const cooperationsRecorded = data.cooperationOutcomes.filter((outcome) => outcome.outcomeType === "completed" || outcome.outcomeType === "cooperation_started").length;
  for (const event of data.cooperationEvents.filter((item) => item.eventStatus === "closed")) {
    entries.push({
      id: `cooperation-${event.id}`,
      kind: "cooperation",
      title: `Cross-border cooperation · ${event.jurisdictionFrom} → ${event.jurisdictionTo}`,
      detail: `Level ${event.level} · ${event.cooperationType}`,
      outcome: "Recorded",
      occurredAt: event.closedAt ?? event.updatedAt,
    });
  }

  const admittedFirms = data.firms.filter((firm) => firm.admittedAt);
  for (const firm of admittedFirms) {
    entries.push({
      id: `admission-${firm.id}`,
      kind: "admission",
      title: `${firm.name} admitted`,
      detail: `${firm.jurisdiction} · ${firm.membershipCategory}`,
      outcome: "Member admitted",
      occurredAt: firm.admittedAt as number,
      href: `/members/${firm.id}`,
    });
  }

  const memberFirmCount = data.firms.filter((firm) => firm.status === "active" && firm.membershipCategory !== "Observer").length;
  const activeProjectLimit = Math.floor(memberFirmCount / 2);
  const snapshotByProposal = new Map(data.voteResultSnapshots.map((snapshot) => [snapshot.proposalId, snapshot]));
  const projects: RegistryProject[] = data.proposals.map((proposal) => {
    const discussion = data.discussions.find((item) => item.id === proposal.discussionId);
    const snapshot = snapshotByProposal.get(proposal.id);
    const deadlinePassed = proposal.votingClosesAt <= Date.now();
    const status: RegistryProjectStatus = deadlinePassed && snapshot?.finalOutcome === "adopted"
      ? "charter"
      : deadlinePassed && snapshot?.finalOutcome === "rejected"
        ? "declined"
        : "active";
    const commits = data.amendments
      .filter((amendment) => amendment.proposalId === proposal.id)
      .sort((a, b) => b.createdAt - a.createdAt)
      .map((amendment) => ({
        id: amendment.id,
        label: `#${amendment.amendmentNumber} ${amendment.type}`,
        summary: amendment.proposedText,
        status: (["approved"] as string[]).includes(amendment.status) ? "merged" as const : (["rejected", "auto_rejected", "withdrawn"] as string[]).includes(amendment.status) ? "declined" as const : "pending" as const,
        authorFirm: data.firms.find((firm) => firm.id === amendment.sponsorFirmId)?.name ?? "Member firm",
        occurredAt: amendment.updatedAt,
      }));
    return {
      id: proposal.id,
      title: discussion?.title ?? "Governance project",
      summary: proposal.consolidatedText ?? proposal.proposalText,
      status,
      deadline: proposal.votingClosesAt,
      patronFirms: proposal.coSponsorFirmIds.map((firmId) => data.firms.find((firm) => firm.id === firmId)?.name ?? firmId),
      commits,
      href: `/discussions/proposals/${proposal.id}`,
    };
  }).sort((a, b) => b.deadline - a.deadline);
  const activeProjectCount = projects.filter((project) => project.status === "active").length;
  entries.sort((a, b) => b.occurredAt - a.occurredAt);

  return {
    decisionsAdopted,
    decisionsRejected,
    mandatesApproved,
    outreachLogged: outreachEvents.length,
    cooperationsRecorded,
    admissions: admittedFirms.length,
    activeProjectLimit,
    activeProjectCount,
    projects,
    entries,
  };
}
